import { API_BASE_URL } from '../../config/api';

const parseErrorResponse = async (response, fallbackMessage) => {
  let errorMessage = fallbackMessage;

  try {
    const data = await response.json();
    if (typeof data?.detail === 'string') {
      errorMessage = data.detail;
    } else if (typeof data?.message === 'string') {
      errorMessage = data.message;
    } else if (typeof data?.error?.message === 'string') {
      errorMessage = data.error.message;
    }
  } catch {
    const text = await response.text().catch(() => '');
    if (text) {
      errorMessage = text;
    }
  }

  throw new Error(errorMessage);
};

export const formatDate = (timestamp) => {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const fetchCreditBalance = async (userId, token) => {
  const response = await fetch(`${API_BASE_URL}/api/billing/credit-balance/${userId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error('Server response:', response.status, errorText);
    throw new Error(`Server error: ${response.status}`);
  }
  return await response.json();
};

export const fetchUsageHistory = async (userId, token) => {
  const response = await fetch(`${API_BASE_URL}/api/billing/usage/${userId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error('Server response:', response.status, errorText);
    throw new Error(`Server error: ${response.status}`);
  }
  return await response.json();
};
export const fetchAutoReloadSettings = async (token) => {
  const response = await fetch(`${API_BASE_URL}/api/payment/auto-reload`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    const errorText = await response.text();
    console.error('Server response:', response.status, errorText);
    throw new Error(`Server error: ${response.status}`);
  }
  return await response.json();
};
export const fetchSubscriptionDetails = async (userId, token) => {
  const response = await fetch(`${API_BASE_URL}/api/billing/subscription/${userId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error('Server response:', response.status, errorText);
    throw new Error(`Server error: ${response.status}`);
  }
  return await response.json();
};

// Restored fetchUserProfile function
export const fetchUserProfile = async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/user-profile`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      console.error('Failed to fetch user profile for plan type');
      return null;
    }
    return await response.json();
};

export const fetchPaymentCustomerCode = async (token) => {
  const response = await fetch(`${API_BASE_URL}/api/payment/payment-info`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to load payment profile.');
  }

  const data = await response.json();
  return data?.customerCode || null;
};

export const fetchPaymentMethods = async (token) => {
  const response = await fetch(`${API_BASE_URL}/api/payment/payment-methods`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to load payment methods.');
  }

  const data = await response.json();
  return Array.isArray(data?.payment_methods) ? data.payment_methods : [];
};

export const addPaymentMethod = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}/api/payment/payment-methods`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to add payment method.');
  }

  return await response.json();
};

export const updatePaymentMethod = async (token, cardId, payload) => {
  const response = await fetch(`${API_BASE_URL}/api/payment/payment-methods/${cardId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update payment method.');
  }

  return await response.json();
};

export const deletePaymentMethod = async (token, cardId) => {
  const response = await fetch(`${API_BASE_URL}/api/payment/payment-methods/${cardId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to remove payment method.');
  }

  return await response.json();
};
